import { useState } from "react";
import { 
    Avatar, Box, Button, Container, Grid, IconButton, 
    Paper, TextField, Typography, Stack 
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faMapMarkerAlt } from "@fortawesome/free-solid-svg-icons";
import { faFacebookF, faInstagram, faLinkedinIn, faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

// Tus importaciones locales
import { SendEmailFromContactUsForm } from "../../controllers/AuxController";
import QUDProgress from "../../components/progress";
import ResponseComponent from "../../components/responseComponent";

// Estilo minimalista y premium para los inputs
const premiumInputStyle = {
    '& .MuiOutlinedInput-root': {
        borderRadius: 3,
        bgcolor: '#f8fafc',
        transition: 'all 0.3s ease',
        '& fieldset': {
            borderColor: '#e2e8f0',
            borderWidth: '1px',
        },
        '&:hover fieldset': {
            borderColor: '#cbd5e1',
        },
        '&.Mui-focused fieldset': {
            borderColor: 'primary.main',
            borderWidth: '2px',
        },
        '&.Mui-error fieldset': {
            borderColor: 'error.main',
        }
    },
    '& .MuiInputLabel-root': {
        color: '#64748b',
        '&.Mui-focused': {
            color: 'primary.main',
        }
    }
};

export default function ContactUs2(props) {
    const { t } = useTranslation();

    // Estados del formulario
    const [nombre, setNombre] = useState('');
    const [apellido, setApellido] = useState('');
    const [telefono, setTelefono] = useState('');
    const [email, setEmail] = useState('');
    const [servicio, setServicio] = useState('');
    const [ciudad, setCiudad] = useState('');
    const [mensaje, setMensaje] = useState('');

    // Estados de error
    const [errorNombre, setErrorNombre] = useState(false);
    const [errorApellido, setErrorApellido] = useState(false);
    const [errorEmail, setErrorEmail] = useState(false);
    const [errorTelefono, setErrorTelefono] = useState(false);
    const [errorServicio, setErrorServicio] = useState(false);
    const [errorCiudad, setErrorCiudad] = useState(false);
    const [errorMensaje, setErrorMensaje] = useState(false);

    // Estados de UI/UX
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [title, setTitle] = useState('');
    const [message, setMessage] = useState('');
    const [success, setSuccess] = useState(true);
    const [show, setShow] = useState(false);
    const [actionText, setActionText] = useState('Finalizar');

    const formVerification = () => {
        let ban = 0;
        if (nombre === '') { setErrorNombre(true); ban = 1; }
        if (apellido === '') { setErrorApellido(true); ban = 1; }
        if (email === '') { setErrorEmail(true); ban = 1; }
        if (telefono === '') { setErrorTelefono(true); ban = 1; }
        if (servicio === '') { setErrorServicio(true); ban = 1; }
        if (ciudad === '') { setErrorCiudad(true); ban = 1; }
        if (mensaje === '') { setErrorMensaje(true); ban = 1; }
        return ban;
    };

    const handleSubmit = async () => {
        if (formVerification() === 1) return;

        setIsLoadingData(true);
        let response = await SendEmailFromContactUsForm(nombre, apellido, email, telefono, servicio, ciudad, mensaje);
        if (response.success === true) {
            setTitle('Operación Exitosa');
            setMessage(response.message);
            setActionText('Finalizar');
            setSuccess(true);
            setShow(true);
        }
        setIsLoadingData(false);
    };

    const handleSuccess = () => {
        setNombre(''); setErrorNombre(false);
        setApellido(''); setErrorApellido(false);
        setEmail(''); setErrorEmail(false);
        setTelefono(''); setErrorTelefono(false);
        setServicio(''); setErrorServicio(false);
        setCiudad(''); setErrorCiudad(false);
        setMensaje(''); setErrorMensaje(false);
        setShow(false);
    };

    return (
        <Box sx={{ position: 'relative', bgcolor: '#f8fafc', py: { xs: 10, md: 15 }, overflow: 'hidden' }}>
            
            <QUDProgress open={isLoadingData} />
            <ResponseComponent
                show={show}
                title={title}
                message={message}
                success={success}
                actionText={actionText}
                onClick={() => handleSuccess()}
            />

            <Container maxWidth='lg'>
                
                {/* Header de la sección */}
                <Box textAlign="center" mb={8} maxWidth="700px" mx="auto">
                    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                        <Typography variant='h3' component='h2' fontWeight='900' color='text.primary' mb={2}>
                            {props.title || t('contactUs.title2')}
                        </Typography>
                        <Typography variant='h6' color='text.secondary' fontWeight='400' sx={{ lineHeight: 1.6 }}>
                            {props.description || t('contactUs.text')}
                        </Typography>
                    </motion.div>
                </Box>

                {/* Tarjeta Split-Pane Premium */}
                <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}>
                    <Paper elevation={0} sx={{ 
                        borderRadius: 6, 
                        overflow: 'hidden', 
                        boxShadow: '0 25px 50px -12px rgba(0,0,0,0.1)',
                        border: '1px solid #e2e8f0',
                        bgcolor: 'white'
                    }}>
                        <Grid container>
                            
                            {/* PANEL IZQUIERDO: Información de Contacto (Dark Premium) */}
                            <Grid item xs={12} md={5} sx={{ 
                                bgcolor: '#0f172a', 
                                backgroundImage: 'linear-gradient(to bottom right, #0f172a, #1e3a8a)', 
                                color: 'white', 
                                p: { xs: 5, md: 6 },
                                position: 'relative',
                                borderRadius:3
                            }}>
                                {/* Elemento decorativo de fondo */}
                                <Box sx={{ position: 'absolute', bottom: -50, right: -50, opacity: 0.05, transform: 'rotate(-15deg)' }}>
                                    <FontAwesomeIcon icon={faEnvelope} style={{ fontSize: '250px' }} />
                                </Box>

                                <Box position="relative" zIndex={1}>
                                    <Typography variant="h4" fontWeight="800" mb={1}>
                                        {t('contactUs.contactData')}
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: '#94a3b8', mb: 6, lineHeight: 1.6 }}>
                                        Estamos aquí para ayudarte a escalar tu negocio. Escríbenos y nuestro equipo te responderá a la brevedad.
                                    </Typography>

                                    <Stack spacing={4}>
                                        {/* Bloque Correo */}
                                        <Box display="flex" alignItems="flex-start" gap={3}>
                                            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: '#60a5fa', width: 48, height: 48 }}>
                                                <FontAwesomeIcon icon={faEnvelope} />
                                            </Avatar>
                                            <Box>
                                                <Typography variant="overline" color="#94a3b8" fontWeight="700" letterSpacing={1}>{t('contactUs.emailUs')}</Typography>
                                                <Typography variant="h6" fontWeight="600" component="a" href="mailto:contact@qud.life" sx={{ color: 'white', textDecoration: 'none', display: 'block', '&:hover': { color: '#60a5fa' } }}>
                                                    contact@qud.life
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {/* Bloque WhatsApp */}
                                        <Box display="flex" alignItems="flex-start" gap={3}>
                                            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: '#4ade80', width: 48, height: 48 }}>
                                                <FontAwesomeIcon icon={faWhatsapp} fontSize="1.2rem" />
                                            </Avatar>
                                            <Box>
                                                <Typography variant="overline" color="#94a3b8" fontWeight="700" letterSpacing={1}>{t('contactUs.chatWithUs')}</Typography>
                                                <Stack spacing={1} mt={0.5}>
                                                    <Button variant="text" href="https://wa.me/584129442394" target="_blank" sx={{ justifyContent: 'flex-start', p: 0, color: 'white', '&:hover': { color: '#4ade80', bgcolor: 'transparent' }, textTransform: 'none', fontSize: '1rem', fontWeight: '500' }}>
                                                        🇻🇪 +58 412-9442394
                                                    </Button>
                                                    <Button variant="text" href="https://wa.me/573177641410" target="_blank" sx={{ justifyContent: 'flex-start', p: 0, color: 'white', '&:hover': { color: '#4ade80', bgcolor: 'transparent' }, textTransform: 'none', fontSize: '1rem', fontWeight: '500' }}>
                                                        🇨🇴 +57 317-7641410
                                                    </Button>
                                                    <Button variant="text" href="https://wa.me/17869400291" target="_blank" sx={{ justifyContent: 'flex-start', p: 0, color: 'white', '&:hover': { color: '#4ade80', bgcolor: 'transparent' }, textTransform: 'none', fontSize: '1rem', fontWeight: '500' }}>
                                                        🇺🇸 +1 (786) 940-0291
                                                    </Button>
                                                </Stack>
                                            </Box>
                                        </Box>
                                    </Stack>

                                    {/* Redes Sociales */}
                                    <Box mt={8}>
                                        <Typography variant="overline" color="#94a3b8" fontWeight="700" letterSpacing={1} display="block" mb={2}>
                                            {t('contactUs.followUs')}
                                        </Typography>
                                        <Stack direction="row" spacing={2}>
                                            {/*<IconButton href="https://www.facebook.com/qud.life" target="_blank" sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'white', '&:hover': { bgcolor: '#1877F2' }, transition: '0.3s' }}>
                                                <FontAwesomeIcon icon={faFacebookF} fontSize="1rem" />
                                            </IconButton>*/}
                                            <IconButton href="https://instagram.com/anzteenamora" target="_blank" sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'white', '&:hover': { bgcolor: '#E4405F' }, transition: '0.3s' }}>
                                                <FontAwesomeIcon icon={faInstagram} fontSize="1rem" />
                                            </IconButton>
                                            {/*<IconButton href="https://www.linkedin.com/company/qud-life/" target="_blank" sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'white', '&:hover': { bgcolor: '#0A66C2' }, transition: '0.3s' }}>
                                                <FontAwesomeIcon icon={faLinkedinIn} fontSize="1rem" />
                                            </IconButton>*/}
                                        </Stack>
                                    </Box>
                                </Box>
                            </Grid>

                            {/* PANEL DERECHO: Formulario (Blanco Clean) */}
                            <Grid item xs={12} md={7} sx={{ p: { xs: 4, md: 8 } }}>
                                <Typography variant='h5' component='h3' color='text.primary' fontWeight='800' mb={1}>
                                    {t('contactUs.formTitle')}
                                </Typography>
                                <Typography variant='body1' color='text.secondary' mb={4}>
                                    {t('contactUs.formDescription') || "Completa el siguiente formulario y nos pondremos en contacto."}
                                </Typography>

                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth label={t('forms.name')} name="nombre" value={nombre}
                                            onChange={(e) => setNombre(e.target.value)}
                                            onBlur={() => setErrorNombre(nombre === '')}
                                            error={errorNombre} helperText={errorNombre && 'Ingrese su nombre'}
                                            sx={premiumInputStyle}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth label={t('forms.lastName')} name="apellido" value={apellido}
                                            onChange={(e) => setApellido(e.target.value)}
                                            onBlur={() => setErrorApellido(apellido === '')}
                                            error={errorApellido} helperText={errorApellido && 'Ingrese su apellido'}
                                            sx={premiumInputStyle}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth label={t('forms.email')} name="email" value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            onBlur={() => setErrorEmail(email === '')}
                                            error={errorEmail} helperText={errorEmail && 'Ingrese su correo electrónico'}
                                            sx={premiumInputStyle}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth label={t('forms.phoneNumber')} name="telefono" value={telefono}
                                            onChange={(e) => setTelefono(e.target.value)}
                                            onBlur={() => setErrorTelefono(telefono === '')}
                                            error={errorTelefono} helperText={errorTelefono && 'Ingrese su número'}
                                            sx={premiumInputStyle}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth label={t('forms.subject')} name="servicio" value={servicio}
                                            onChange={(e) => setServicio(e.target.value)}
                                            onBlur={() => setErrorServicio(servicio === '')}
                                            error={errorServicio} helperText={errorServicio && 'Ingrese el Asunto'}
                                            sx={premiumInputStyle}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth label={t('forms.city')} name="ciudad" value={ciudad}
                                            onChange={(e) => setCiudad(e.target.value)}
                                            onBlur={() => setErrorCiudad(ciudad === '')}
                                            error={errorCiudad} helperText={errorCiudad && 'Ingrese su ciudad'}
                                            sx={premiumInputStyle}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth multiline rows={4} label={t('forms.message')} name="mensaje" value={mensaje}
                                            onChange={(e) => setMensaje(e.target.value)}
                                            onBlur={() => setErrorMensaje(mensaje === '')}
                                            error={errorMensaje} helperText={errorMensaje && 'Ingrese su mensaje'}
                                            sx={premiumInputStyle}
                                        />
                                    </Grid>
                                    
                                    <Grid item xs={12} mt={2}>
                                        <Button
                                            fullWidth
                                            size="large"
                                            disabled={isLoadingData}
                                            variant="contained"
                                            onClick={handleSubmit}
                                            sx={{ 
                                                py: 2, 
                                                borderRadius: 3, 
                                                fontSize: '1.1rem', 
                                                fontWeight: '800',
                                                boxShadow: '0 8px 16px rgba(20, 122, 194, 0.25)',
                                                textTransform: 'none'
                                            }}
                                        >
                                            {props.buttonText || 'Enviar Mensaje'}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Paper>
                </motion.div>

            </Container>
        </Box>
    );
}